import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import { CASES } from "@/data/content";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const study = CASES.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (study) {
      document.title = `${study.title[lang] || study.title.id} — Makario`;
    }
  }, [study, lang]);

  useEffect(() => {
    if (!study) return;
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='cs-eyebrow']", { opacity: 0, y: 14, duration: 0.6 });
      gsap.from("[data-anim='cs-meta']", { opacity: 0, y: 18, duration: 0.7, stagger: 0.04, delay: 0.08 });
      gsap.from("[data-anim='cs-title']", { opacity: 0, y: 40, duration: 1.05, ease: "power3.out", delay: 0.18 });
      gsap.from("[data-anim='cs-side']", { opacity: 0, y: 24, duration: 0.95, ease: "power3.out", delay: 0.4 });
      gsap.from("[data-anim='cs-summary']", { opacity: 0, y: 22, duration: 0.85, delay: 0.5 });
      gsap.from("[data-anim='cs-row']", {
        opacity: 0,
        y: 36,
        duration: 0.85,
        stagger: 0.14,
        ease: "power3.out",
        scrollTrigger: { trigger: "[data-anim='cs-body']", start: "top 80%" },
      });
      gsap.from("[data-anim='cs-quote']", {
        opacity: 0,
        y: 30,
        duration: 0.95,
        ease: "power3.out",
        scrollTrigger: { trigger: "[data-anim='cs-quote']", start: "top 85%" },
      });
    });
    return () => ctx.revert();
  }, [study]);

  if (!study) {
    return (
      <main className="min-h-screen flex items-center justify-center text-[var(--ink)]">
        <div className="text-center">
          <p className="h-sub">{t("detail.notFound")}</p>
          <button onClick={() => navigate("/")} className="btn-pill mt-8 text-[var(--ink)] border-[var(--ink)]">
            <ArrowLeft size={14} /> {t("detail.back")}
          </button>
        </div>
      </main>
    );
  }

  const caseIdx = CASES.findIndex((c) => c.slug === study.slug);
  const related = CASES.filter((c) => c.slug !== study.slug);
  const nextCase = CASES[(caseIdx + 1) % CASES.length];
  const accentInk = study.accent === "#a8e063" ? "#1f5733" : study.accent;

  return (
    <main data-testid="case-study-detail" className="min-h-screen">
      <Navbar />

      {/* ───────── HERO — minimal, immersive ───────── */}
      <section
        className="relative min-h-screen flex flex-col px-6 sm:px-8 md:px-20 lg:px-32 pt-36 md:pt-40 pb-16 overflow-hidden grain"
        style={{ background: study.bg }}
      >
        {/* One soft ambient orb */}
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: 1100,
            height: 1100,
            top: "-30%",
            right: "-25%",
            background: `radial-gradient(circle, ${study.accent} 0%, transparent 60%)`,
            opacity: 0.10,
            filter: "blur(50px)",
          }}
        />

        {/* Top eyebrow */}
        <div
          data-anim="cs-eyebrow"
          className="relative z-10 flex items-center justify-between mb-16 md:mb-24"
        >
          <Link
            to="/#works"
            data-testid="cs-back"
            className="inline-flex items-center gap-2 text-white/65 hover:text-white text-[0.62rem] md:text-[0.65rem] font-mono uppercase tracking-[0.28em] transition-colors"
          >
            <ArrowLeft size={14} /> {t("detail.back")}
          </Link>
          <div className="flex items-center gap-4 md:gap-6 font-mono text-[0.6rem] md:text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
            <span>{String(caseIdx + 1).padStart(2, "0")} / {String(CASES.length).padStart(2, "0")}</span>
            <span className="hidden md:inline">{t("works.caseStudy") || "Case Study"}</span>
          </div>
        </div>

        {/* Composition — generous whitespace, no watermark */}
        <div className="relative z-10 flex-1 grid grid-cols-12 gap-x-6 md:gap-x-12 lg:gap-x-16 gap-y-10 items-center">
          {/* Tiny accent line + year — anchor */}
          <div
            data-anim="cs-meta"
            className="col-span-12 md:col-span-2 self-start flex items-center gap-3"
          >
            <span className="inline-block h-px w-6" style={{ background: study.accent }} />
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/55">
              {study.year}
            </span>
          </div>

          {/* Title — restrained, dignified */}
          <h1
            data-anim="cs-title"
            data-testid="cs-title"
            className="col-span-12 md:col-span-10 md:col-start-3 font-display text-white"
            style={{
              fontSize: "clamp(2rem, 5.4vw, 5.2rem)",
              lineHeight: 0.98,
              letterSpacing: "-0.035em",
              fontWeight: 600,
              maxWidth: "20ch",
            }}
          >
            {study.title[lang]}
          </h1>

          {/* Synopsis — under title, indented */}
          <p
            data-anim="cs-summary"
            className="col-span-12 md:col-span-7 md:col-start-3 text-white/85 leading-[1.65]"
            style={{ fontSize: "clamp(15px, 1.05vw, 17px)", maxWidth: "38rem" }}
          >
            {study.summary[lang]}
          </p>

          {/* Tiny meta strip on the right — replaces the loud details card */}
          <div
            data-anim="cs-side"
            className="col-span-12 md:col-span-3 md:col-start-10 md:self-end font-mono text-[0.58rem] uppercase tracking-[0.28em] text-white/55 space-y-3"
          >
            <div className="flex items-baseline justify-between gap-4">
              <span>{t("detail.client")}</span>
              <span className="text-white/85 normal-case tracking-normal font-sans text-sm">{study.client}</span>
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <span>{t("detail.industry")}</span>
              <span className="text-white/85 normal-case tracking-normal font-sans text-sm">{study.industry[lang]}</span>
            </div>
            <div className="flex items-baseline justify-between gap-4 pt-3 border-t border-white/12">
              <span>{t("detail.impact")}</span>
              <span
                className="normal-case tracking-normal font-sans text-sm font-medium"
                style={{ color: study.accent }}
              >
                {study.metric[lang]}
              </span>
            </div>
          </div>
        </div>

        {/* Bottom rule */}
        <div className="relative z-10 mt-12 pt-5 border-t border-white/12 flex items-center justify-between font-mono text-[0.55rem] md:text-[0.6rem] uppercase tracking-[0.28em] text-white/40">
          <span>{lang === "id" ? "Studi kasus Makario" : "A Makario case study"}</span>
          <span>{study.client}</span>
        </div>
      </section>

      {/* ───────── BODY — restrained editorial chapters ───────── */}
      <section
        className="relative px-6 sm:px-8 md:px-20 lg:px-32 py-28 md:py-40"
        style={{ background: "var(--paper)" }}
        data-anim="cs-body"
      >
        <div className="max-w-[1180px] mx-auto">
          {/* CHAPTER 01 — Challenge */}
          <div
            data-anim="cs-row"
            className="grid grid-cols-12 gap-x-6 md:gap-x-12 gap-y-6 mb-28 md:mb-40"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="flex items-center gap-3">
                <span className="inline-block h-px w-6" style={{ background: accentInk }} />
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                  01 · {t("detail.challenge")}
                </span>
              </div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h2
                className="font-display text-[var(--ink)]"
                style={{
                  fontSize: "clamp(1.6rem, 2.6vw, 2.4rem)",
                  letterSpacing: "-0.025em",
                  fontWeight: 600,
                  lineHeight: 1.05,
                  maxWidth: "20ch",
                }}
              >
                {lang === "id" ? "Kondisi awal." : "The starting line."}
              </h2>
              <p
                className="mt-8 md:mt-10 text-black/65 leading-[1.8] max-w-[44rem]"
                style={{ fontSize: "clamp(15px, 1.1vw, 17px)" }}
              >
                {study.challenge[lang]}
              </p>
            </div>
          </div>

          {/* CHAPTER 02 — Approach */}
          <div
            data-anim="cs-row"
            className="grid grid-cols-12 gap-x-6 md:gap-x-12 gap-y-6 mb-28 md:mb-40"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="flex items-center gap-3">
                <span className="inline-block h-px w-6" style={{ background: accentInk }} />
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                  02 · {t("detail.approach")}
                </span>
              </div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h2
                className="font-display text-[var(--ink)]"
                style={{
                  fontSize: "clamp(1.6rem, 2.6vw, 2.4rem)",
                  letterSpacing: "-0.025em",
                  fontWeight: 600,
                  lineHeight: 1.05,
                  maxWidth: "20ch",
                }}
              >
                {lang === "id" ? "Bagaimana kami menjalankannya." : "How we ran it."}
              </h2>
              <p
                className="mt-8 md:mt-10 text-black/65 leading-[1.8] max-w-[44rem]"
                style={{ fontSize: "clamp(15px, 1.1vw, 17px)" }}
              >
                {study.approach[lang]}
              </p>
            </div>
          </div>

          {/* PULL QUOTE — minimal, centered */}
          <div
            data-anim="cs-quote"
            className="my-28 md:my-40 py-14 md:py-20 border-y border-black/10 flex flex-col items-center text-center"
          >
            <span
              className="inline-block h-px w-10 mb-8"
              style={{ background: accentInk }}
            />
            <blockquote
              className="font-display text-[var(--ink)] max-w-[50rem]"
              style={{
                fontSize: "clamp(1.3rem, 2.4vw, 2rem)",
                lineHeight: 1.25,
                letterSpacing: "-0.022em",
                fontWeight: 500,
              }}
            >
              <span style={{ color: accentInk, fontWeight: 600 }}>{study.metric[lang]}</span>
              {" — "}
              {lang === "id"
                ? "buah dari pendekatan yang berdisiplin pada konteks lokal."
                : "the fruit of a disciplined, context-aware approach."}
            </blockquote>
            <div className="mt-8 font-mono text-[0.55rem] uppercase tracking-[0.32em] text-black/40">
              — {study.client}, {study.year}
            </div>
          </div>

          {/* CHAPTER 03 — Outcome */}
          <div
            data-anim="cs-row"
            className="grid grid-cols-12 gap-x-6 md:gap-x-12 gap-y-6 mb-28 md:mb-40"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="flex items-center gap-3">
                <span className="inline-block h-px w-6" style={{ background: accentInk }} />
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                  03 · {t("detail.outcome")}
                </span>
              </div>
              <div
                className="mt-8 inline-flex items-center gap-2 px-3.5 py-2 rounded-full"
                style={{ background: study.accent }}
              >
                <span
                  className="font-mono text-[0.55rem] uppercase tracking-[0.28em]"
                  style={{ color: study.accentText }}
                >
                  ↑
                </span>
                <span
                  className="text-[13px]"
                  style={{ color: study.accentText, fontWeight: 600 }}
                >
                  {study.metric[lang]}
                </span>
              </div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h2
                className="font-display text-[var(--ink)]"
                style={{
                  fontSize: "clamp(1.6rem, 2.6vw, 2.4rem)",
                  letterSpacing: "-0.025em",
                  fontWeight: 600,
                  lineHeight: 1.05,
                  maxWidth: "20ch",
                }}
              >
                {lang === "id" ? "Hasil terukur." : "Measurable outcomes."}
              </h2>
              <p
                className="mt-8 md:mt-10 text-black/65 leading-[1.8] max-w-[44rem]"
                style={{ fontSize: "clamp(15px, 1.1vw, 17px)" }}
              >
                {study.outcome[lang]}
              </p>
            </div>
          </div>

          {/* CTA strip — quieter */}
          <div
            data-anim="cs-row"
            className="relative rounded-2xl overflow-hidden p-10 md:p-16 mb-28"
            style={{ background: "var(--ink)" }}
          >
            <div
              className="absolute pointer-events-none rounded-full"
              style={{
                width: 600,
                height: 600,
                top: "-50%",
                right: "-15%",
                background: `radial-gradient(circle, ${study.accent} 0%, transparent 60%)`,
                opacity: 0.16,
                filter: "blur(35px)",
              }}
            />
            <div className="relative flex flex-col md:flex-row gap-10 md:items-end md:justify-between">
              <div className="text-white max-w-[36rem]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-block h-px w-6" style={{ background: study.accent }} />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-white/55">
                    {lang === "id" ? "Diskusi" : "Discuss"}
                  </span>
                </div>
                <p
                  className="font-display text-white"
                  style={{
                    fontSize: "clamp(1.4rem,2.2vw,1.9rem)",
                    letterSpacing: "-0.022em",
                    fontWeight: 500,
                    lineHeight: 1.22,
                  }}
                >
                  {lang === "id"
                    ? "Punya tantangan serupa? Mari diskusikan ruang lingkup yang relevan untuk konteks Anda."
                    : "Facing a similar challenge? Let's discuss what the right scope looks like for your context."}
                </p>
              </div>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-3 self-start md:self-auto font-mono text-[0.62rem] md:text-[0.65rem] uppercase tracking-[0.28em] text-white border border-white/30 rounded-full px-5 py-3 hover:bg-white hover:text-black transition-all duration-300"
              >
                {t("nav.cta")} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* NEXT CASE — restrained */}
          <div data-anim="cs-row" className="mb-28">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block h-px w-6 bg-black/40" />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                {lang === "id" ? "Selanjutnya" : "Up next"}
              </span>
            </div>
            <Link
              to={`/works/${nextCase.slug}`}
              data-testid={`cs-next-${nextCase.slug}`}
              className="group block relative rounded-2xl overflow-hidden transition-all duration-500"
              style={{ background: nextCase.bg }}
            >
              <div
                className="absolute pointer-events-none rounded-full"
                style={{
                  width: 700,
                  height: 700,
                  top: "-45%",
                  right: "-20%",
                  background: `radial-gradient(circle, ${nextCase.accent} 0%, transparent 60%)`,
                  opacity: 0.14,
                  filter: "blur(35px)",
                }}
              />
              <div className="relative grid grid-cols-12 gap-x-6 p-10 md:p-16 min-h-[280px] items-end">
                <div className="col-span-12 md:col-span-8 text-white">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="inline-block h-px w-6" style={{ background: nextCase.accent }} />
                    <span className="font-mono text-[0.55rem] uppercase tracking-[0.32em] text-white/55">
                      {nextCase.year} · {nextCase.industry[lang]}
                    </span>
                  </div>
                  <h3
                    className="font-display text-white transition-transform duration-500 group-hover:translate-x-1"
                    style={{
                      fontSize: "clamp(1.5rem, 3vw, 2.4rem)",
                      letterSpacing: "-0.028em",
                      fontWeight: 600,
                      lineHeight: 1.04,
                      maxWidth: "20ch",
                    }}
                  >
                    {nextCase.title[lang]}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-4 flex md:justify-end items-end mt-6 md:mt-0">
                  <span className="inline-flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.28em] text-white/90 group-hover:text-white">
                    {t("works.viewCase")}
                    <ArrowUpRight
                      size={16}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Related list — minimal */}
          <div data-anim="cs-row">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block h-px w-6 bg-black/40" />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/45">
                {lang === "id" ? "Studi Kasus Lainnya" : "Other case studies"}
              </span>
            </div>
            <ol className="border-t border-black/10">
              {related.map((c, i) => (
                <li key={c.slug} className="border-b border-black/10">
                  <Link
                    to={`/works/${c.slug}`}
                    data-testid={`cs-related-${c.slug}`}
                    className="group flex items-center justify-between gap-5 md:gap-6 py-6"
                  >
                    <div className="flex items-baseline gap-5 md:gap-7 min-w-0">
                      <span
                        className="font-mono text-[0.6rem] uppercase tracking-[0.28em] flex-none"
                        style={{ color: c.accent === "#a8e063" ? "#1f5733" : c.accent }}
                      >
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="font-display text-[var(--ink)] truncate transition-all duration-300 group-hover:translate-x-1"
                        style={{
                          fontSize: "clamp(1.05rem, 1.8vw, 1.5rem)",
                          letterSpacing: "-0.018em",
                          fontWeight: 500,
                        }}
                      >
                        {c.title[lang]}
                      </span>
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="text-black/30 group-hover:text-[var(--violet)] transition-colors flex-none"
                    />
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
